import type { AuthUser } from '../interfaces'
import type { ActiveCredit } from '../interfaces/credit.interface'

import { nordyAxios } from './client'
import channelTalkService from '../services/channelTalk.service' //!nordy
import { useIsReadonlyStore } from '../store/nordyIsReadonlyStore' //!nordy
import { fileUrlByAsset } from '../utils/fileBucketUrl'
import { waitForMixpanelAdditionalInfo } from '../utils/mixpanelAdditionalInfo'
import {
  getOrCreateAttributionContext,
  toMixpanelAttributionProperties
} from '../utils/mixpanelAttribution'
import { mixpanelRuntime } from '../utils/mixpanelRuntime'
import { registerMixpanelLoggedOutUser } from '../utils/mixpanelUserProperties'
import type { MixpanelAdditionalInfo } from '../utils/mixpanelAdditionalInfo'
import type { OAuthMixpanelEventProperties } from '../utils/mixpanelTypes'
import {
  clearOAuthAttributionSnapshot,
  getOAuthMixpanelTrackingSnapshot
} from '../utils/mixpanelOAuthAttribution'
import { trackNordyLogout } from '../utils/gtm'

interface GoogleAuthBody {
  code: string
}

type OAuthMixpanelAdditionalInfo = MixpanelAdditionalInfo &
  OAuthMixpanelEventProperties

interface GoogleAuthRequestBody extends GoogleAuthBody {
  mixpanelAdditionalInfo: OAuthMixpanelAdditionalInfo
}

interface DiscordAuthBody {
  code: string
  redirect: string
}

interface DiscordAuthRequestBody extends DiscordAuthBody {
  mixpanelAdditionalInfo: OAuthMixpanelAdditionalInfo
}

export const authApi = {
  getActiveCredit: async () => {
    const { data } = await nordyAxios.get<ActiveCredit>('/v1/credit/active')
    return data
  },

  getUser: async (): Promise<AuthUser | null> => {
    const { data: user } = await nordyAxios.get<AuthUser | false>('/v1/auth')
    if (!user) return null

    //!nordy - 무구독=Free(v2): denorm 미러필드 참조 + 무구독 폴백 'Free'(채널톡 표시 일관성).
    const subscription = user.subscriptionItemName ?? 'Free'

    //!nordy - channelTalkService를 통해 인증된 사용자로 boot
    //!nordy - iframe readonly 모드(?readonly=true)에서는 채널톡 비활성화
    if (!useIsReadonlyStore().isReadonly) {
      channelTalkService.boot({
        pluginKey: '96c0ac68-f183-4a35-a865-c07ce5068ae7',
        memberId: `${user._id}`,
        profile: {
          name: user.nameTag,
          email: user.email,
          subscription: subscription,
          avatarUrl: user.profile
            ? fileUrlByAsset({ asset: user.profile, quality: 'w200' })
            : user.socialProfile
        }
      })
    }

    return user
  },

  loginByEmailPassword: async ({
    email,
    password
  }: {
    email: string
    password: string
  }) => {
    const { data } = await nordyAxios.post<AuthUser>(
      '/v1/auth/login/email-password',
      {
        email,
        password
      }
    )
    return data
  },

  signupOrLoginByDiscord: async (body: DiscordAuthBody) => {
    const { mixpanelAdditionalInfo, oauthEventProperties } =
      await getOAuthMixpanelTrackingSnapshot({
        fallback: waitForMixpanelAdditionalInfo
      })
    const requestBody: DiscordAuthRequestBody = {
      ...body,
      mixpanelAdditionalInfo: {
        ...mixpanelAdditionalInfo,
        ...oauthEventProperties
      }
    }
    const { data } = await nordyAxios.post<AuthUser>(
      '/v1/auth/signup/discord',
      requestBody
    )
    clearOAuthAttributionSnapshot()
    return data
  },

  signupOrLoginByGoogle: async (body: GoogleAuthBody) => {
    const { mixpanelAdditionalInfo, oauthEventProperties } =
      await getOAuthMixpanelTrackingSnapshot({
        fallback: waitForMixpanelAdditionalInfo
      })
    const requestBody: GoogleAuthRequestBody = {
      ...body,
      mixpanelAdditionalInfo: {
        ...mixpanelAdditionalInfo,
        ...oauthEventProperties
      }
    }
    const { data } = await nordyAxios.post<AuthUser>(
      '/v1/auth/signup/google',
      requestBody
    )
    clearOAuthAttributionSnapshot()
    return data
  },

  logout: async () => {
    await nordyAxios.post('/v1/auth/logout')
    mixpanelRuntime.events.logoutSuccess()
    const context = getOrCreateAttributionContext({
      url: new URL(window.location.href),
      referrer: document.referrer
    })
    registerMixpanelLoggedOutUser(toMixpanelAttributionProperties(context))
    trackNordyLogout()
    window.location.reload()
  }
}
